import React from 'react'
import {
    InputHLPure,
    TextAreaHLPure,
    ButtonHLPure,
    Stepper,
    InstructionsPure,
    CheckBoxHLPure,
    LabelHLPure,
    DataPickerHLPure,
    DropDownBtnHLPure,
    TooltipHLPure,
    GridSelectionHLPure,
    ButtonAddHLPure
} from '../'
import { useTranslation } from 'react-i18next'
import './alertConfigurationModal.css'
import { DateTime } from "luxon"
import { InteractiveDialog } from '../index'
import { InteractiveContainer, InteractiveItem } from '../../layouts'
import LabelPure from '../label/label.pure'
import moment from 'moment'

const TOMORROW = DateTime.now().plus({ days: 1 }).endOf('day').toJSDate()

/**
 * @typedef {Object} AlertConfigurationModalProps
 * @property {boolean} isOpen - Indicates if the modal is open
 * @property {number} currentStep - Current step in the stepper
 * @property {Function} setCurrentStep - Function to set the current step
 * @property {() => void} handleNext - Function to go to the next step
 * @property {() => void} handlePrevious - Function to go to the previous step
 * @property {{title: string, icon: string}[]} steps - Array of step labels for the stepper
 * @property {function(): boolean} validateData - Function to validate the form data
 * @property {() => void} onClose - Function to call when closing the modal
 * @property {() => void} onSave - Function to call when saving the alert configuration
 * @property {string} [documentId] - ID of the document associated with the alert
 * @property {string[]} [fieldsWithError] - List of form fields with validation errors (optional)
 * @property {import('../../../types/alerts.types').AlertState} initialData - Initial data for editing an existing alert (optional)
 * @property {(e: React.ChangeEvent<HTMLInputElement> | Date | any, field: string) => void} handleInputChange - Function to handle input changes in the form (optional)
 * @property {(e: React.ChangeEvent<HTMLInputElement> | Date | any) => void} handleDailyRecurrenceChange - Function to handle changes in daily recurrence settings (optional)
 * @property {(e: React.ChangeEvent<HTMLInputElement> | Date | any) => void} handleWeeklyRecurrenceChange - Function to handle changes in weekly recurrence settings (optional)
 * @property {(e: React.ChangeEvent<HTMLInputElement> | Date | any, type: string, field: string) => void} handleMonthlyRecurrenceChange - Function to handle changes in monthly recurrence settings (optional)
 * @property {(e: React.ChangeEvent<HTMLInputElement> | Date | any, type: string, field: string) => void} handleYearlyRecurrenceChange - Function to handle changes in yearly recurrence settings (optional)
 * @property {(docType: import('../../../types/alerts.types').AlertDocumentVariable) => void} [handleAddDocumentType] - Function to handle adding a document type (optional)
 * @property {(docTypeId: number) => void} [handleRemoveDocumentType] - Function to handle removing a document type (optional)
 * @property {(value: any, field: string) => void} [handleDocumentTypeChange] - Function to handle changes to document type fields (optional)
 * @property {import('../../../types/document-type.types').Variable[]} [typesDocuments] - Array of document types data (optional)
 */

/**
 * AlertConfigurationModalPure component
 * @param {AlertConfigurationModalProps} Props
 */
const AlertConfigurationModalPure = ({
    isOpen,
    onClose,
    fieldsWithError,
    onSave,
    currentStep,
    steps,
    handleNext,
    handlePrevious,
    initialData,
    handleInputChange,
    handleDailyRecurrenceChange,
    handleWeeklyRecurrenceChange,
    handleMonthlyRecurrenceChange,
    handleYearlyRecurrenceChange,
    handleAddDocumentType,
    handleRemoveDocumentType,
    handleDocumentTypeChange,
    typesDocuments = []
}) => {

    const { t } = useTranslation()

    const weekdays = moment.weekdays();
    const weekdayOptions = weekdays.map((weekday, i) => ({
        id: i,
        name: weekday
    }))

    const months = moment.months();
    const monthOptions = months.map((month, i) => ({
        id: i,
        name: month
    }))

    const ordinalNumberOptions = [{
        id: 1,
        name: t('alertDetails.ordinal.first')
    }, {
        id: 2,
        name: t('alertDetails.ordinal.second')
    }, {
        id: 3,
        name: t('alertDetails.ordinal.third')
    }, {
        id: 4,
        name: t('alertDetails.ordinal.fourth')
    }]

    const isFirstStep = currentStep === 0
    const isLastStep = currentStep === steps.length - 1

    const existingDocumentVariableIds = (initialData.alertDocumentVariables || []).map(dv => dv.documentVariableId)
    const availableTypesDocuments = Array.isArray(typesDocuments)
        ? typesDocuments.filter(dt => dt?.documentVariable?.id != null && !existingDocumentVariableIds.includes(dt.documentVariable.id))
        : []
    const selectPlaceholder = t('alertConfiguration.personalizedData.select.placeholder')

    const dropdownTypesDocuments = [
        { id: 0, name: selectPlaceholder },
        ...(Array.isArray(availableTypesDocuments)
            ? availableTypesDocuments.filter(dt => dt?.documentVariable?.id != null)
            : [])
    ]

    const selectedValue = initialData.documentType?.id ?? 0

    /** 
     * @param {{dataItem: {id: number, name: string, comments: string}}} item
     */
    const CellColumnOne = (item) => (<td><div>
        <p className='grid-cell-titles-margin-left'>{item.dataItem.name}</p>
    </div></td>)

    /** 
     * @param {{dataItem: {id: number, name: string, comments: string}}} item
     */
    const CellColumnTwo = (item) => (
        <td>
            <div className='alertConfigurationModal-cell'>
                {item.dataItem.comments}
            </div>
        </td>
    )

    // Columna de acciones (por ejemplo, eliminar)
    /**
     * @param {{dataItem: {id: number, name: string, comments: string, documentVariableId?: number}}} item
     */
    const CellColumnActions = (item) => (
        <td className='alertConfigurationModal-cell-actions'>
            {handleRemoveDocumentType && (
                <div className='alertConfigurationModal-cell-actions.container'>

                    <button
                        className='cellActions-TrashBtn'
                        onClick={() => handleRemoveDocumentType(item.dataItem.documentVariableId || item.dataItem.id)}
                        data-id={'labelTrashActions'}
                    >
                        <i title={t('documentPanelBarAlerts.alerts.delete')} className='k-icon k-i-delete k-i-trash'></i>
                    </button>
                </div>
            )}
        </td>
    )

    /**
     * @param {{title: string}} props
     */
    const AllHeadersColumns = ({ title }) => (
        <span className='documentPanelBarAlerts-header-cell-one'>{title}</span>
    )

    // Header para la columna de acciones
    const HeaderColumnActions = ({ title }) => (
        <span className='documentPanelBarAlerts-header-cell-one'>{title}</span>
    )

    const addDocumentType = () => {
        if (handleAddDocumentType && initialData.documentType?.id && initialData.documentType.id !== 0) {
            // Find the selected document type from typesDocuments
            const selectedDocType = typesDocuments.find(dt => dt.id === initialData.documentType?.id);

            // Check if the document type is already added
            const existing = initialData.alertDocumentVariables || []
            const selectedDocVarId = selectedDocType?.documentVariable?.id
            const isAlreadyAdded = selectedDocVarId != null && existing.some(dv => dv.documentVariableId === selectedDocVarId)
            if (isAlreadyAdded) return

            if (selectedDocType && selectedDocType.documentVariable) {
                /** @type {import('../../../types/alerts.types').AlertDocumentVariable} */
                const docTypeToAdd = {
                    documentVariableId: selectedDocType.documentVariable.id,
                    name: selectedDocType.name,
                    comments: initialData.documentType?.comments || ''
                };
                handleAddDocumentType(docTypeToAdd);
                if (handleDocumentTypeChange) {
                    handleDocumentTypeChange(0, 'id')
                    handleDocumentTypeChange('', 'comments')
                }
            }
        }
    }

    const renderStepContent = () => {
        const totalDocVars = Array.isArray(initialData.alertDocumentVariables) ? initialData.alertDocumentVariables.length : 0
        let heightGrid = '180px'
        if (totalDocVars === 0 || totalDocVars === 1) heightGrid = '110px'
        else heightGrid = '180px'
        switch (currentStep) {
            case 0:
                return (
                    <>
                        <InteractiveItem item xs={12}>
                            <InstructionsPure
                                instructions={[t("alertConfiguration.generalData.instructions")]}
                            />
                        </InteractiveItem>
                        <InteractiveItem item xs={12}>
                            <InputHLPure
                                labelInput={t('alertDetails.recipients')}
                                inputActive
                                required
                                typeInput='text'
                                valueInput={initialData.emailList}
                                handleChangeInput={
                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                    (e) => handleInputChange(e.target.value, 'emailList')
                                }
                                tooltipShow
                                informationIcon
                                positionTooltip={'top'}
                                titleTooltip={t('alertDetails.recipients')}
                                descriptionTooltip={t('alertDetails.errors.separe.emails')}
                                errorInput={fieldsWithError?.includes('emailList')}
                                errorInputText={t('alertDetails.errors.enter.valid.emails')}
                                characterLimit={128}
                            />
                        </InteractiveItem>
                        <InteractiveItem item xs={12}>
                            <InputHLPure
                                labelInput={t('alertDetails.messageSubject')}
                                placeholder={t('alertDetails.messageSubjectPlaceholder')}
                                inputActive
                                required
                                typeInput='text'
                                valueInput={initialData.name}
                                handleChangeInput={
                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                    (e) => handleInputChange(e.target.value, 'name')
                                }
                                errorInput={fieldsWithError?.includes('name')}
                                errorInputText={t('alertDetails.errors.enter.name')}
                                characterLimit={128}
                            />
                        </InteractiveItem>
                        <InteractiveItem item xs={12}>
                            <TextAreaHLPure
                                labelTextArea={t('alertDetails.description')}
                                valueTextData={initialData.description}
                                placeholder={t('alertDetails.descriptionPlaceholder')}
                                required
                                handleChangeTextArea={
                                    /** @param {React.ChangeEvent<HTMLTextAreaElement>} e */
                                    (e) => handleInputChange(e.target.value, 'description')
                                }
                                errorInput={fieldsWithError?.includes('description')}
                                errorTextArea={t('alertDetails.errors.enter.description')}
                                characterLimit={500}
                            />
                        </InteractiveItem>
                        <InteractiveItem item xs={12}>
                            <CheckBoxHLPure
                                labelCheckBox={t('alertDetails.includeURL')}
                                checkedState={initialData.includeURL}
                                onChangeHandler={
                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                    (e) => handleInputChange(e.target.checked, 'includeURL')
                                }
                                tooltipShow
                                positionTooltip={'top'}
                                titleTooltip={t('alertDetails.includeURL')}
                                descriptionTooltip={`${t('alertDetails.includeURLDescription')} ${t('alertDetails.includeURLSubContent')} ${t('alertDetails.includeURLRestContent')}`}
                                informationIcon
                            />
                            {fieldsWithError?.includes('includeURL') && <div style={{ color: 'red', fontSize: '0.75rem', marginTop: '4px' }}>{t('alertDetails.errors.includeURL')}</div>}
                        </InteractiveItem>
                    </>
                )
            case 1:
                return (
                    <InteractiveContainer>
                        <InteractiveItem>
                            <InstructionsPure
                                instructions={[t("alertConfiguration.range.instructions")]}
                            />
                        </InteractiveItem>
                        <InteractiveItem>
                            <LabelHLPure text={t('alertDetails.range')} fontSize='20px' />
                        </InteractiveItem>
                        <InteractiveItem sm={6}>
                            <LabelPure title={t('alertDetails.start.date')} />
                        </InteractiveItem>
                        <InteractiveItem sm={6}>
                            <DataPickerHLPure
                                id='alert-startDate'
                                valueDate={initialData.startDate}
                                handleChangeDate={
                                    /** @param {{ value: Date }} e */
                                    (e) => handleInputChange(e.value, 'startDate')
                                }
                                limitDate={TOMORROW}
                                errorInput={fieldsWithError?.includes('startDate')}
                                errorDatePicker={t('alertDetails.enter.date.tomorrow')}
                            />
                        </InteractiveItem>
                        <InteractiveItem sm={6}>
                            <CheckBoxHLPure
                                labelCheckBox={t('alertDetails.by.limit.date')}
                                idCheckBox={'fechaTerminacion'}
                                valueCheckBox={'1'}
                                checkedState={initialData.type === '1'}
                                onChangeHandler={
                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                    (e) => handleInputChange(e.target.value, 'type')
                                }
                            />
                        </InteractiveItem>
                        <InteractiveItem sm={6}>
                            {
                                initialData.type === '1' &&
                                <DataPickerHLPure
                                    valueDate={initialData.endDate}
                                    handleChangeDate={
                                        /** @param {{ value: Date }} e */
                                        (e) => handleInputChange(e.value, 'endDate')
                                    }
                                    limitDate={initialData?.startDate || TOMORROW}
                                    errorInput={fieldsWithError?.includes('endDate')}
                                    errorDatePicker={t('alertDetails.enter.after.day')}
                                />
                            }
                        </InteractiveItem>
                        <InteractiveItem sm={6}>
                            <CheckBoxHLPure
                                labelCheckBox={t('alertDetails.by.occurrences')}
                                idCheckBox={'ocurrencias'}
                                valueCheckBox={'2'}
                                checkedState={initialData.type === '2'}
                                onChangeHandler={
                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                    (e) => handleInputChange(e.target.value, 'type')
                                }
                            />
                        </InteractiveItem>
                        <InteractiveItem item xs={12} sm={6}>
                            {
                                initialData.type === '2' &&
                                <InputHLPure
                                    inputActive
                                    typeInput='number'
                                    pattern='[0-9]*'
                                    inputmode='numeric'
                                    placeholder='0'
                                    valueInput={initialData.ocurrences}
                                    handleChangeInput={
                                        /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                        (e) => handleInputChange(e.target.value, 'ocurrences')
                                    }
                                    min={1}
                                    max={999}
                                    step={1}
                                    errorInput={fieldsWithError?.includes('ocurrences')}
                                    errorInputText={t('alertDetails.enter.number.greater.zero')}
                                />
                            }
                        </InteractiveItem>
                        <InteractiveItem>
                            <hr />
                        </InteractiveItem>
                        <InteractiveItem>
                            <LabelHLPure text={t('alertDetails.alert.frequency')} fontSize='20px' />
                        </InteractiveItem>
                        <InteractiveItem container sm={3} spacing={1} alignContent='flex-start'>
                            <InteractiveItem>
                                <CheckBoxHLPure
                                    labelCheckBox={t('alertDetails.daily')}
                                    idCheckBox={'rec-1'}
                                    valueCheckBox={'1'}
                                    checkedState={initialData.recurrenceType === 1}
                                    onChangeHandler={
                                        /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                        (e) => handleInputChange(+e.target.value, 'recurrenceType')
                                    }
                                />
                            </InteractiveItem>
                            <InteractiveItem>
                                <CheckBoxHLPure
                                    labelCheckBox={t('alertDetails.weekly')}
                                    idCheckBox={'rec-2'}
                                    valueCheckBox={'2'}
                                    checkedState={initialData.recurrenceType === 2}
                                    onChangeHandler={
                                        /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                        (e) => handleInputChange(+e.target.value, 'recurrenceType')
                                    }
                                />
                            </InteractiveItem>
                            <InteractiveItem>
                                <CheckBoxHLPure
                                    labelCheckBox={t('alertDetails.monthly')}
                                    idCheckBox={'rec-3'}
                                    valueCheckBox={'3'}
                                    checkedState={initialData.recurrenceType === 3}
                                    onChangeHandler={
                                        /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                        (e) => handleInputChange(+e.target.value, 'recurrenceType')
                                    }
                                />
                            </InteractiveItem>
                            <InteractiveItem>
                                <CheckBoxHLPure
                                    labelCheckBox={t('alertDetails.annual')}
                                    idCheckBox={'rec-4'}
                                    valueCheckBox={'4'}
                                    checkedState={initialData.recurrenceType === 4}
                                    onChangeHandler={
                                        /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                        (e) => handleInputChange(+e.target.value, 'recurrenceType')
                                    }
                                />
                            </InteractiveItem>
                        </InteractiveItem>
                        <InteractiveItem container alignContent='flex-start' spacing={2}
                            sm={9}
                            className='alertDetails-recurrence-inputs'
                        >
                            {
                                initialData.recurrenceType === 1 &&
                                <InteractiveItem>
                                    <label className='alertDetails-inline-elements'>
                                        {t('alertDetails.every')}
                                        <InputHLPure
                                            id='rec1-times'
                                            inputActive
                                            typeInput='number'
                                            pattern='[0-9]*'
                                            inputmode='numeric'
                                            max={999}
                                            min={1}
                                            step={1}
                                            placeholder='0'
                                            valueInput={initialData.dailyRecurrence}
                                            handleChangeInput={handleDailyRecurrenceChange}
                                        />
                                        {t('alertDetails.days')}
                                        <TooltipHLPure
                                            positionTooltip={'top'}
                                            titleTooltip={t('alertDetails.days.tooltip.title')}
                                            descriptionTooltip={t('alertDetails.days.tooltip.description')}
                                            informationIcon
                                        />
                                    </label>
                                    {
                                        fieldsWithError?.includes('times') && <div>
                                            <p className='hint-message-error'>{t('alertDetails.enter.number.greater.zero')}</p>
                                        </div>
                                    }
                                </InteractiveItem>
                            }
                            {
                                initialData.recurrenceType === 2 &&
                                <>
                                    <InteractiveItem container>
                                        <label className='alertDetails-inline-elements'>
                                            {t('alertDetails.every')}
                                            <InputHLPure
                                                id='rec2-times'
                                                inputActive
                                                typeInput='number'
                                                pattern='[0-9]*'
                                                inputmode='numeric'
                                                max={99}
                                                min={1}
                                                step={1}
                                                placeholder='0'
                                                valueInput={initialData.weeklyRecurrence.times}
                                                handleChangeInput={handleWeeklyRecurrenceChange}
                                            />
                                            {t('alertDetails.weeks')}
                                            <TooltipHLPure
                                                positionTooltip={'top'}
                                                titleTooltip={t('alertDetails.weeks.tooltip.title')}
                                                descriptionTooltip={t('alertDetails.weeks.tooltip.description')}
                                                informationIcon
                                            />
                                        </label>
                                        {
                                            fieldsWithError?.includes('times') && <div>
                                                <p className='hint-message-error'>{t('alertDetails.enter.number.greater.zero')}</p>
                                            </div>
                                        }
                                    </InteractiveItem>
                                    <InteractiveItem container>
                                        <div className='alertDetails-checkboxes'>{
                                            weekdays.map((weekday, i) => <CheckBoxHLPure
                                                labelCheckBox={weekday}
                                                checkedState={initialData.weeklyRecurrence.weekDays.includes(i)}
                                                valueCheckBox={initialData.weeklyRecurrence.weekDays.includes(i)}
                                                idCheckBox={`rec2-weekday${i}`}
                                                onChangeHandler={handleWeeklyRecurrenceChange}
                                            />)
                                        }
                                        </div>
                                        {
                                            fieldsWithError?.includes('weekDays') && <div>
                                                <p className='hint-message-error'>{t('alertDetails.errors.select.day')}</p>
                                            </div>
                                        }
                                    </InteractiveItem>
                                </>
                            }
                            {
                                initialData.recurrenceType === 3 &&
                                <>
                                    <InteractiveItem container>
                                        <label className='alertDetails-inline-elements'>
                                            <CheckBoxHLPure
                                                idCheckBox={'rec3-type1'}
                                                valueCheckBox={'1'}
                                                checkedState={initialData.monthlyRecurrence.type === '1'}
                                                onChangeHandler={
                                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                                    (e) => handleMonthlyRecurrenceChange(e, '', 'type')
                                                }
                                            />
                                            {t('alertDetails.monthly.day')}
                                            <InputHLPure
                                                id='rec3-day'
                                                inputActive
                                                typeInput='number'
                                                pattern='[0-9]*'
                                                inputmode='numeric'
                                                max={31}
                                                min={1}
                                                step={1}
                                                placeholder='0'
                                                disabled={initialData.monthlyRecurrence.type !== '1'}
                                                valueInput={initialData.monthlyRecurrence.byDay.day}
                                                handleChangeInput={
                                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                                    e => handleMonthlyRecurrenceChange(e, '1', 'day')
                                                }
                                            />
                                            {t('alertDetails.monthly.every')}
                                            <InputHLPure
                                                id='rec3-times'
                                                inputActive
                                                typeInput='number'
                                                pattern='[0-9]*'
                                                inputmode='numeric'
                                                max={36}
                                                min={1}
                                                step={1}
                                                placeholder='0'
                                                disabled={initialData.monthlyRecurrence.type !== '1'}
                                                valueInput={initialData.monthlyRecurrence.byDay.times}
                                                handleChangeInput={
                                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                                    e => handleMonthlyRecurrenceChange(e, '1', 'times')
                                                }
                                            />
                                            {t('alertDetails.monthly.months')}
                                            <TooltipHLPure
                                                positionTooltip={'top'}
                                                titleTooltip={t('alertDetails.monthly.tolltip.title')}
                                                descriptionTooltip={t('alertDetails.errors.enter.number')}
                                                informationIcon
                                            />
                                        </label>
                                        {
                                            fieldsWithError?.includes('day') && <div>
                                                <p className='hint-message-error'>{t('alertDetails.errors.enter.day')}</p>
                                            </div>
                                        }
                                        {
                                            fieldsWithError?.includes('times') && <div>
                                                <p className='hint-message-error'>{t('alertDetails.enter.number.greater.zero')}</p>
                                            </div>
                                        }
                                    </InteractiveItem>
                                    <InteractiveItem container>
                                        <label className='alertDetails-inline-elements'>
                                            <CheckBoxHLPure
                                                idCheckBox={'rec3-type2'}
                                                valueCheckBox={'2'}
                                                checkedState={initialData.monthlyRecurrence.type === '2'}
                                                onChangeHandler={
                                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                                    (e) => handleMonthlyRecurrenceChange(e, '2', 'type')
                                                }
                                            />
                                            {t('alertDetails.monthly.the')}
                                            <DropDownBtnHLPure
                                                optionsDropDown={ordinalNumberOptions}
                                                valueItem={'id'}
                                                nameOption={'name'}
                                                disable={initialData.monthlyRecurrence.type !== '2'}
                                                optionsValue={initialData.monthlyRecurrence.byOrdinalNumber.ordinalNumber}
                                                onDropDownChange={
                                                    /** @param {React.ChangeEvent<HTMLSelectElement>} e */
                                                    e => handleMonthlyRecurrenceChange(e, '2', 'ordinalNumber')
                                                }
                                            />
                                            <DropDownBtnHLPure
                                                optionsDropDown={weekdayOptions}
                                                valueItem={'id'}
                                                nameOption={'name'}
                                                disable={initialData.monthlyRecurrence.type !== '2'}
                                                optionsValue={initialData.monthlyRecurrence.byOrdinalNumber.weekDays}
                                                onDropDownChange={
                                                    /** @param {React.ChangeEvent<HTMLSelectElement>} e */
                                                    e => handleMonthlyRecurrenceChange(e, '2', 'weekDays')
                                                }
                                                position='top'
                                            />
                                            {t('alertDetails.monthly.every')}
                                            <InputHLPure
                                                inputActive
                                                typeInput='number'
                                                pattern='[0-9]*'
                                                inputmode='numeric'
                                                placeholder='0'
                                                max={36}
                                                min={1}
                                                step={1}
                                                disabled={initialData.monthlyRecurrence.type !== '2'}
                                                valueInput={initialData.monthlyRecurrence.byOrdinalNumber.times}
                                                handleChangeInput={
                                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                                    e => handleMonthlyRecurrenceChange(e, '2', 'times')
                                                }
                                            />
                                            {t('alertDetails.monthly.months')}
                                            <TooltipHLPure
                                                positionTooltip={'top'}
                                                titleTooltip={t('alertDetails.monthly.tolltip.title')}
                                                descriptionTooltip={t('alertDetails.errors.enter.number')}
                                                informationIcon
                                            />
                                        </label>
                                        {
                                            fieldsWithError?.includes('times') && <div>
                                                <p className='hint-message-error'>{t('alertDetails.enter.number.greater.zero')}</p>
                                            </div>
                                        }
                                    </InteractiveItem>
                                </>
                            }
                            {
                                initialData.recurrenceType === 4 &&
                                <>
                                    <InteractiveItem container>
                                        <label className='alertDetails-inline-elements'>
                                            {t('alertDetails.annual.every')}
                                            <InputHLPure
                                                inputActive
                                                typeInput='number'
                                                pattern='[0-9]*'
                                                inputmode='numeric'
                                                min={1}
                                                max={999}
                                                step={1}
                                                placeholder='0'
                                                valueInput={initialData.yearlyRecurrence.times}
                                                handleChangeInput={
                                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                                    (e) => handleYearlyRecurrenceChange(e, '', 'times')
                                                }
                                            />
                                            {t('alertDetails.annual.years')}
                                        </label>
                                        {
                                            fieldsWithError?.includes('times') && <div>
                                                <p className='hint-message-error'>{t('alertDetails.enter.number.greater.zero')}</p>
                                            </div>
                                        }
                                    </InteractiveItem>
                                    <InteractiveItem container>
                                        <label className='alertDetails-inline-elements'>
                                            <CheckBoxHLPure
                                                idCheckBox={'anual-type1'}
                                                valueCheckBox={'1'}
                                                checkedState={initialData.yearlyRecurrence.type === '1'}
                                                onChangeHandler={
                                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                                    (e) => handleYearlyRecurrenceChange(e, '', 'type')
                                                }
                                            />
                                            {t('alertDetails.monthly.the')}
                                            <InputHLPure
                                                inputActive
                                                typeInput='number'
                                                pattern='[0-9]*'
                                                inputmode='numeric'
                                                max={31}
                                                min={1}
                                                step={1}
                                                disabled={initialData.yearlyRecurrence.type !== '1'}
                                                placeholder='0'
                                                valueInput={initialData.yearlyRecurrence.byDay.day}
                                                handleChangeInput={
                                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                                    (e) => handleYearlyRecurrenceChange(e, '1', 'day')
                                                }
                                            />
                                            <DropDownBtnHLPure
                                                optionsDropDown={monthOptions}
                                                valueItem={'id'}
                                                nameOption={'name'}
                                                disable={initialData.yearlyRecurrence.type !== '1'}
                                                optionsValue={initialData.yearlyRecurrence.byDay.month}
                                                onDropDownChange={
                                                    /** @param {React.ChangeEvent<HTMLSelectElement>} e */
                                                    e => handleYearlyRecurrenceChange(e, '1', 'month')
                                                }
                                            />
                                        </label>
                                        {
                                            fieldsWithError?.includes('day') && <div>
                                                <p className='hint-message-error'>{t('alertDetails.errors.enter.valid.year')}</p>
                                            </div>
                                        }
                                    </InteractiveItem>
                                    <InteractiveItem container>
                                        <label className='alertDetails-inline-elements'>
                                            <CheckBoxHLPure
                                                idCheckBox={'anual-type2'}
                                                valueCheckBox={'2'}
                                                checkedState={initialData.yearlyRecurrence.type === '2'}
                                                onChangeHandler={
                                                    /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                                    (e) => handleYearlyRecurrenceChange(e, '', 'type')
                                                }
                                            />
                                            {t('alertDetails.monthly.the')}
                                            <DropDownBtnHLPure
                                                optionsDropDown={ordinalNumberOptions}
                                                valueItem={'id'}
                                                nameOption={'name'}
                                                disable={initialData.yearlyRecurrence.type !== '2'}
                                                optionsValue={initialData.yearlyRecurrence.byOrdinalNumber.ordinalNumber}
                                                onDropDownChange={
                                                    /** @param {React.ChangeEvent<HTMLSelectElement>} e */
                                                    e => handleYearlyRecurrenceChange(e, '2', 'ordinalNumber')
                                                }
                                            />
                                            <DropDownBtnHLPure
                                                optionsDropDown={weekdayOptions}
                                                valueItem={'id'}
                                                nameOption={'name'}
                                                disable={initialData.yearlyRecurrence.type !== '2'}
                                                optionsValue={initialData.yearlyRecurrence.byOrdinalNumber.weekDays}
                                                onDropDownChange={
                                                    /** @param {React.ChangeEvent<HTMLSelectElement>} e */
                                                    e => handleYearlyRecurrenceChange(e, '2', 'weekDays')
                                                }
                                                position={'top'}
                                            />
                                            {t('alertDetails.of')}
                                            <DropDownBtnHLPure
                                                optionsDropDown={monthOptions}
                                                valueItem={'id'}
                                                nameOption={'name'}
                                                disable={initialData.yearlyRecurrence.type !== '2'}
                                                optionsValue={initialData.yearlyRecurrence.byOrdinalNumber.month}
                                                onDropDownChange={
                                                    /** @param {React.ChangeEvent<HTMLSelectElement>} e */
                                                    e => handleYearlyRecurrenceChange(e, '2', 'month')
                                                }
                                                position={'top'}
                                            />
                                        </label>
                                    </InteractiveItem>
                                </>
                            }
                        </InteractiveItem>
                    </InteractiveContainer>
                )
            case 2:
                return (
                    <>
                        <InteractiveItem>
                            <InstructionsPure instructions={[t("alertConfiguration.personalizedData.instructions")]} />
                        </InteractiveItem>
                        <InteractiveItem>
                            <DropDownBtnHLPure
                                labelDropDown={t("alertConfiguration.personalizedData.availableData")}
                                optionsDropDown={dropdownTypesDocuments}
                                onDropDownChange={
                                    /** @param {React.ChangeEvent<HTMLSelectElement>} e */
                                    (e) => handleDocumentTypeChange && handleDocumentTypeChange(e.target.value, 'id')
                                }
                                optionsValue={selectedValue}
                                valueItem={"id"}
                                nameOption='name'
                            />
                            {fieldsWithError?.includes('documentType') && (
                                <div style={{ color: 'red', fontSize: '0.75rem', marginTop: '4px' }}>
                                    {t("alertConfiguration.personalizedData.document.error")}
                                </div>
                            )}
                        </InteractiveItem>
                        <InteractiveItem>
                            <TextAreaHLPure
                                labelTextArea={t("alertConfiguration.personalizedData.comments")}
                                placeholder={t("alertConfiguration.personalizedData.comments.placeholder")}
                                characterLimit={500}
                                handleChangeTextArea={
                                    /** @param {React.ChangeEvent<HTMLTextAreaElement>} e */
                                    (e) => handleDocumentTypeChange && handleDocumentTypeChange(e.target.value, 'comments')
                                }
                                valueTextData={initialData.documentType?.comments || ""}
                                errorInput={fieldsWithError?.includes('documentTypeComments')}
                                errorTextArea={t('alertDetails.errors.commentsWithoutType')}
                            />
                        </InteractiveItem>
                        <InteractiveItem className='alert-details-personalized-data-button'>
                            <ButtonAddHLPure
                                nameBtn={t("alertConfiguration.personalizedData.button.title")}
                                iconAdd
                                onClickBtn={addDocumentType}
                                customIcon=""
                                disabled={!initialData.documentType?.id || initialData.documentType.id === 0}
                            />
                        </InteractiveItem>
                        <InteractiveItem>
                            <div className='alertConfigurationModal-grid'>
                                <GridSelectionHLPure
                                    styleGeneral={true}
                                    styleGeneralClass={'alertConfigurationModal-general-grid'}
                                    pageable={false}
                                    skip={0}
                                    take={totalDocVars}
                                    dataGrid={initialData.alertDocumentVariables || []}
                                    columnTwo={true}
                                    columnThree={true}
                                    titleRow1={t("alertConfiguration.personalizedData.grid.title.1")}
                                    titleRow2={t("alertConfiguration.personalizedData.comments")}
                                    titleRow3={t("alertConfiguration.personalizedData.grid.actions")}
                                    cellColumnOne={CellColumnOne}
                                    headerColumnOne={AllHeadersColumns}
                                    minResizableWidth1={'auto'}
                                    cellColumnTwo={CellColumnTwo}
                                    headerColumnTwo={AllHeadersColumns}
                                    cellColumnThree={CellColumnActions}
                                    headerColumnThree={HeaderColumnActions}
                                    styleGrid={{ height: heightGrid }}
                                />
                            </div>
                        </InteractiveItem>
                    </>
                )
            default:
                return null
        }
    }

    const renderNavigationButtons = () => {
        return (
            <>
                <ButtonHLPure
                    nameBtn={t("button.cancel")}
                    onClickBtn={onClose}
                />
                {!isFirstStep && (
                    <ButtonHLPure
                        nameBtn={t("button.return")}
                        onClickBtn={handlePrevious}
                    />
                )}
                {!isLastStep ? (
                    <ButtonHLPure
                        nameBtn={t("button.continue")}
                        onClickBtn={handleNext}
                        primary
                    />
                ) : (
                    <ButtonHLPure
                        nameBtn={t("button.save")}
                        onClickBtn={onSave}
                        primary
                    />
                )}
            </>
        )
    }

    if (!isOpen) return null

    return (
        <InteractiveDialog title={Object.values(initialData).every(Boolean) ? t("documentPanelBarAlerts.dialog.edit.title", { step: steps[currentStep]?.title }) : t("documentPanelBarAlerts.dialog.new.title", { step: steps[currentStep]?.title })}
            onClose={onClose}
            onSave={onSave}
            CustomButtons={renderNavigationButtons()}
        >
            <Stepper
                steps={steps}
                selectedStep={currentStep}
            />
            {renderStepContent()}
        </InteractiveDialog>
    )
}

export default AlertConfigurationModalPure
